import { safeAccessibilityRole } from '@/constants/utils/a11y';
import React, { useMemo } from 'react';
import { Platform, ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import MetaHead from '@/components/MetaHead';
import PublicFooter from '@/components/PublicFooter';
import useLandingStyles from '@/components/Landing/useLandingStyles';
import { landingPalette } from '@/components/Landing/landingTokens';
import AboutHero from '@/components/About/AboutHero';
import AboutStory from '@/components/About/AboutStory';
import AboutWorkspace from '@/components/About/AboutWorkspace';
import AboutClosing from '@/components/About/AboutClosing';
import { isDarkTheme } from '@/constants/Colors';
import { useTheme } from '@/context/ThemeContext';
import { GAB_AI_SOFTWARE_ID } from '@/constants/config';
import { MODEL_TOTAL, PROVIDER_TOTAL } from '@/constants/aboutContent';

/** Company purpose, principles, and products in the landing page's visual style. */
export default function AboutPage() {
  const { theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const insets = useSafeAreaInsets();

  useLandingStyles();

  const containerStyle = useMemo(() => ({
    flex: 1,
    backgroundColor: landingPalette(isDark).page,
  }), [isDark]);

  return (
    <View style={containerStyle}>
      <MetaHead
        title="About Gab AI"
        description="More time for the people you love. Gab AI helps us produce more, prosper more, and still get home."
        canonical="/about"
        keywords="about Gab AI, all-in-one AI, AI search, AI file management, AI image generator, AI video generator, AI music, AI voice chat, AI code assistant, AI studios, AI tools, ChatGPT alternative, private AI, Made in USA"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: 'About Gab AI',
          description: `Learn about the company behind Gab AI Work and ${MODEL_TOTAL} AI models from ${PROVIDER_TOTAL} providers. Built in Pennsylvania with a commitment to free expression, privacy, and useful software.`,
          url: 'https://gab.ai/about',
          mainEntity: {
            '@type': 'Organization',
            name: 'Gab AI Inc.',
            foundingDate: '2016',
            foundingLocation: 'Pennsylvania, USA',
            // Same node as the site-wide one in `app/+html.jsx`, referenced by
            // @id so the two merge rather than competing.
            makesOffer: {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'SoftwareApplication',
                '@id': GAB_AI_SOFTWARE_ID,
                name: 'Gab AI',
              },
            },
          },
        }}
      />

      <ScrollView
        {...(Platform.OS === 'web' ? { dataSet: { landingScroll: 'true' } } : {})}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        accessibilityRole={safeAccessibilityRole('main')}
        nativeID="main-content"
        style={{ position: 'relative', zIndex: 1 }}
      >
        <AboutHero topInset={insets.top} />
        <AboutStory />
        <AboutWorkspace />
        <AboutClosing />
        <PublicFooter variant="editorial" showHero={false} />
      </ScrollView>
    </View>
  );
}
